/**
 * useRecordChanges Hook
 * Manages change indicators and last modified timestamps for records
 */

import { useState, useEffect } from 'react';
import { batchGetLastModified } from '../utils/recordTracker';

/**
 * Hook to track changes for a list of records
 * @param {Array} records - Array of records with id field
 * @param {string} resourceType - Type of resource (default: 'sales')
 * @param {boolean} enabled - Whether to track changes (default: true)
 * @returns {Object} Object containing lastModified map and loading state
 */
export const useRecordChanges = (records = [], resourceType = 'sales', enabled = true) => {
  const [lastModified, setLastModified] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled || !records.length) {
      setLastModified({});
      return;
    }

    const fetchLastModified = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Extract record IDs
        const recordIds = records
          .map(record => record.transactionId || record.id)
          .filter(Boolean);
        
        if (recordIds.length === 0) {
          setLastModified({});
          return;
        }

        const results = await batchGetLastModified(recordIds, resourceType);
        setLastModified(results);
      } catch (err) {
        console.error('Error fetching record changes:', err);
        setError(err);
        setLastModified({});
      } finally {
        setLoading(false);
      }
    };

    fetchLastModified();
  }, [records, resourceType, enabled]);

  /**
   * Get last modified date for a specific record
   * @param {string} recordId - ID of the record
   * @returns {Date|null} Last modified date or null
   */
  const getLastModifiedForRecord = (recordId) => {
    return lastModified[recordId] || null;
  };

  /**
   * Check if a record was modified recently
   * @param {string} recordId - ID of the record
   * @param {number} hoursThreshold - Hours to consider recent (default: 24)
   * @returns {boolean} True if recently modified
   */
  const isRecentlyModified = (recordId, hoursThreshold = 24) => {
    const date = getLastModifiedForRecord(recordId);
    if (!date) return false;
    
    const now = new Date();
    const diffHours = (now - date) / (1000 * 60 * 60);
    return diffHours <= hoursThreshold;
  };

  /**
   * Refresh the last modified data
   */
  const refresh = () => {
    if (!enabled || !records.length) return;
    
    const recordIds = records
      .map(record => record.transactionId || record.id)
      .filter(Boolean);
    
    if (recordIds.length > 0) {
      batchGetLastModified(recordIds, resourceType)
        .then(setLastModified)
        .catch(setError);
    }
  };

  return {
    lastModified,
    loading,
    error,
    getLastModifiedForRecord,
    isRecentlyModified,
    refresh
  };
};

export default useRecordChanges;
